// src/components/mpi/D3RiskDrillDown.js
import { useEffect, useState, useRef, useCallback } from 'react';
import { mentoringApi as axios } from '../../axiosInstance';

function AnimatedBar({ value, color, delay = 0 }) {
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const id = setTimeout(() => setWidth(value ?? 0), delay + 60);
    return () => clearTimeout(id);
  }, [value, delay]);
  return (
    <div style={{ flex: 1, height: 6, borderRadius: 3, background: 'rgba(255,255,255,0.07)', overflow: 'hidden' }}>
      <div style={{
        height: '100%', borderRadius: 3,
        width: `${width}%`,
        background: color,
        boxShadow: `0 0 8px ${color}66`,
        transition: 'width 0.6s cubic-bezier(0.4,0,0.2,1)',
      }} />
    </div>
  );
}

function riskColor(score) {
  if (score >= 60) return '#ef4444';
  if (score >= 40) return '#f59e0b';
  return '#22c55e';
}

const SIGNAL_LABELS = {
  no_session:        'Kein Gespräch',
  survey_low:        'Umfrage niedrig',
  critical_role:     'Kritische Rolle',
  tenure_window:     'Tenure-Fenster',
  lead_change:       'FK-Wechsel',
  retention_survey:  'Retention-Umfrage',
};

const SIGNAL_COLORS = {
  no_session:        '#ef4444',
  survey_low:        '#f59e0b',
  critical_role:     '#f97316',
  tenure_window:     '#8b5cf6',
  lead_change:       '#3b82f6',
  retention_survey:  '#6b7280',
};

export default function D3RiskDrillDown({ period: initialPeriod, tenant, onClose }) {
  const [data, setData]           = useState(null);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState('');
  const [selectedDept, setSelectedDept] = useState(null);
  const [checkin, setCheckin] = useState(null);
  const boxRef = useRef(null);

  const fetchData = useCallback((dept) => {
    setLoading(true);
    setError('');
    const params = { withCredentials: true };
    let url = '/mpi/risk-details';
    const qp = [];
    if (initialPeriod) qp.push(`period=${encodeURIComponent(initialPeriod)}`);
    if (tenant) qp.push(`tenant=${encodeURIComponent(tenant)}`);
    if (dept) qp.push(`department=${encodeURIComponent(dept)}`);
    if (qp.length) url += '?' + qp.join('&');

    axios.get(url, params)
      .then(res => setData(res.data))
      .catch(() => setError('Risiko-Daten konnten nicht geladen werden.'))
      .finally(() => setLoading(false));
  }, [initialPeriod, tenant]);

  useEffect(() => { fetchData(null); }, [fetchData]);

  // B3: Check-in-Signale (additiv, eigener Fetch; Contract wirft nie -> catch -> null).
  useEffect(() => {
    let alive = true;
    const tq = tenant ? `?tenant=${encodeURIComponent(tenant)}` : '';
    axios.get(`/mpi/checkin-summary${tq}`, { withCredentials: true })
      .then(r => { if (alive) setCheckin(r.data ?? null); })
      .catch(() => { if (alive) setCheckin(null); });
    return () => { alive = false; };
  }, [tenant]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  function handleOverlayClick(e) {
    if (boxRef.current && !boxRef.current.contains(e.target)) onClose();
  }

  function handleDeptClick(deptName) {
    if (selectedDept === deptName) {
      setSelectedDept(null);
      fetchData(null);
    } else {
      setSelectedDept(deptName);
      fetchData(deptName);
    }
  }

  // ── Data extraction ──
  const period       = data?.period ?? initialPeriod ?? '';
  const prevPeriod   = data?.previous_period ?? '';
  const totalEmp     = data?.total_employees ?? 0;
  const dist         = (selectedDept && data?.filtered?.distribution) || data?.distribution || {};
  const signals      = (selectedDept && data?.filtered?.signals) || data?.signals || {};
  const actions      = (selectedDept && data?.filtered?.actions) || data?.actions || [];
  const departments  = data?.departments ?? [];

  const high   = dist.high   ?? {};
  const medium = dist.medium ?? {};
  const low    = dist.low    ?? {};
  const highCount   = high.count   ?? 0;
  const mediumCount = medium.count ?? 0;
  const lowCount    = low.count    ?? 0;
  const totalDist   = highCount + mediumCount + lowCount;
  const highPct     = high.pct   ?? (totalDist > 0 ? highCount / totalDist * 100 : 0);
  const mediumPct   = medium.pct ?? (totalDist > 0 ? mediumCount / totalDist * 100 : 0);
  const lowPct      = low.pct   ?? (totalDist > 0 ? lowCount / totalDist * 100 : 0);

  const avgRisk     = data?.avg_risk_score != null ? parseFloat(data.avg_risk_score) : null;
  const prevAvgRisk = data?.prev_avg_risk_score != null ? parseFloat(data.prev_avg_risk_score) : null;
  const riskDelta   = avgRisk != null && prevAvgRisk != null ? avgRisk - prevAvgRisk : null;

  // Bug C: Survey-only-NL. Backend liefert mode:'survey', total_employees:null,
  // d3_survey_score, note — und KEINE individuellen Risk-Koepfe. Mentoring-NLs
  // (mode:'mentoring') durchlaufen unveraendert den bestehenden Pfad.
  const surveyMode    = data?.mode === 'survey';
  const d3SurveyScore = data?.d3_survey_score != null ? parseFloat(data.d3_survey_score) : null;
  const surveyNote    = data?.note ?? null;
  const d3SurveyColor = d3SurveyScore == null ? '#64748B'
    : d3SurveyScore >= 60 ? '#22c55e' : d3SurveyScore >= 30 ? '#f59e0b' : '#ef4444';
  // Etappe 2: Funktions-Split aus dem Survey-Zweig von /risk-details.
  const funktionSplit = data?.funktion_split_d3 ?? null;

  return (
    <div
      onClick={handleOverlayClick}
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(0,0,0,0.72)',
        backdropFilter: 'blur(8px)',
        zIndex: 9999,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 20,
      }}
    >
      <div
        ref={boxRef}
        onClick={e => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: 920,
          maxHeight: '88vh',
          background: 'linear-gradient(160deg, #111827 0%, #080b12 100%)',
          border: '1px solid rgba(16,185,129,0.2)',
          borderRadius: 20,
          display: 'flex', flexDirection: 'column',
          boxShadow: '0 32px 80px rgba(0,0,0,0.8), 0 0 0 1px rgba(255,255,255,0.04)',
          overflow: 'hidden',
        }}
      >
        {/* ── Header ── */}
        <div style={{
          padding: '20px 24px 16px',
          borderBottom: '1px solid rgba(255,255,255,0.06)',
          display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
          flexShrink: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 8, height: 8, borderRadius: '50%',
              background: '#10B981', boxShadow: '0 0 10px #10B981',
              flexShrink: 0, marginTop: 5,
            }} />
            <div>
              <div style={{ fontSize: 18, fontWeight: 700, color: 'white', letterSpacing: '-0.3px' }}>
                D3 · Retention &amp; Risiko – Drill-Down
              </div>
              <div style={{ marginTop: 5, display: 'flex', gap: 14, fontSize: 10, color: 'rgba(255,255,255,0.35)' }}>
                {period && <span>{period}</span>}
                {surveyMode
                  ? <span>Anonyme Kulturumfrage</span>
                  : <span>{totalEmp} Mitarbeiter gesamt</span>}
                {!surveyMode && highCount > 0 && <span style={{ color: '#ef4444' }}>{highCount} HIGH Risk</span>}
                {selectedDept && (
                  <span style={{ color: '#3b82f6' }}>Filter: {selectedDept}</span>
                )}
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'rgba(255,255,255,0.07)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: 8, color: 'rgba(255,255,255,0.45)',
              cursor: 'pointer', padding: '6px 12px', fontSize: 11, flexShrink: 0,
            }}
          >
            ✕ Schließen
          </button>
        </div>

        {/* ── Body (scrollable) ── */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>
          {loading && (
            <div style={{ padding: 40, textAlign: 'center', fontSize: 12, color: 'rgba(255,255,255,0.28)', letterSpacing: '0.18em' }}>
              LADE RISIKO-DATEN …
            </div>
          )}
          {error && (
            <div style={{ padding: 40, textAlign: 'center', fontSize: 12, color: '#ef4444' }}>{error}</div>
          )}

          {!loading && !error && (
            <>
              {/* ── Block 1: Survey-Hinweis (survey-only) ODER Risikoverteilung (mentoring) ── */}
              {surveyMode ? (
                <div style={{
                  marginBottom: 24, padding: '20px',
                  background: 'rgba(255,255,255,0.03)',
                  border: '1px solid rgba(255,255,255,0.07)',
                  borderRadius: 14,
                }}>
                  <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.28)', letterSpacing: '0.2em', textTransform: 'uppercase', marginBottom: 16 }}>
                    Retention (Kulturumfrage)
                  </div>
                  {(() => {
                    const fs = funktionSplit;
                    const barColor = (v) => v == null ? '#64748B' : v >= 70 ? '#22c55e' : v >= 40 ? '#f59e0b' : '#ef4444';
                    if (fs && fs.verfuegbar === true && Array.isArray(fs.funktionen) && fs.funktionen.length > 0) {
                      // verfuegbar:true -> Funktionsbalken + NUR Contract-hinweis (alte Notiz ausgeblendet)
                      return (
                        <>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 12 }}>
                            {fs.funktionen.map((f, i) => {
                              const v = f.wert != null ? Number(f.wert) : null;
                              return (
                                <div key={f.funktion ?? i} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                  <div title={f.gruppe ? `${f.funktion} (${f.gruppe})` : f.funktion}
                                       style={{ width: 130, fontSize: 11, color: 'rgba(255,255,255,0.6)', flexShrink: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {f.funktion}
                                  </div>
                                  <AnimatedBar value={v ?? 0} color={barColor(v)} delay={i * 60} />
                                  <div style={{ fontSize: 11, color: barColor(v), fontWeight: 600, minWidth: 78, textAlign: 'right' }}>
                                    {v != null ? v.toFixed(1).replace('.', ',') : '—'} <span style={{ color: 'rgba(255,255,255,0.3)', fontWeight: 400 }}>· n={f.n ?? 0}</span>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                          {fs.hinweis && (
                            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', lineHeight: 1.6 }}>{fs.hinweis}</div>
                          )}
                        </>
                      );
                    }
                    // verfuegbar:false ODER kein Split -> grosse Zahl + alte Notiz (wie bisher) + grund-Hinweis
                    const grundText = fs && fs.verfuegbar === false
                      ? (fs.grund === 'unter_schwelle' ? 'Stichprobe zu klein für Aufschlüsselung' : 'keine Funktionsdaten')
                      : null;
                    return (
                      <>
                        {d3SurveyScore != null && (
                          <div style={{ marginBottom: 16 }}>
                            <div style={{
                              fontSize: 48, fontWeight: 800, letterSpacing: '-2px', lineHeight: 1,
                              color: d3SurveyColor,
                              fontFamily: "'SF Pro Display', -apple-system, sans-serif",
                            }}>
                              {d3SurveyScore.toFixed(0)}
                            </div>
                            <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.3)', marginTop: 4 }}>
                              D3 Retention-Score
                            </div>
                          </div>
                        )}
                        <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', lineHeight: 1.6 }}>
                          Retention basiert auf anonymer Kulturumfrage — keine individuellen Risk-Köpfe.{surveyNote ? ` ${surveyNote}` : ''}
                        </div>
                        {grundText && (
                          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', lineHeight: 1.6, marginTop: 6 }}>{grundText}</div>
                        )}
                      </>
                    );
                  })()}
                </div>
              ) : (
              <div style={{
                marginBottom: 24, padding: '20px',
                background: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.07)',
                borderRadius: 14,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                  <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.28)', letterSpacing: '0.2em', textTransform: 'uppercase' }}>
                    Risikoverteilung
                  </div>
                  {prevPeriod && (
                    <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.2)' }}>
                      vs. {prevPeriod}
                    </div>
                  )}
                </div>

                {/* Ø Risk Score + Trend */}
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: 16, marginBottom: 20 }}>
                  {avgRisk != null && (
                    <>
                      <div>
                        <div style={{
                          fontSize: 48, fontWeight: 800, letterSpacing: '-2px', lineHeight: 1,
                          color: riskColor(avgRisk),
                          fontFamily: "'SF Pro Display', -apple-system, sans-serif",
                        }}>
                          {avgRisk.toFixed(1)}
                        </div>
                        <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.3)', marginTop: 4 }}>
                          Ø Risk Score
                        </div>
                      </div>
                      {riskDelta != null && (
                        <div style={{
                          display: 'flex', alignItems: 'center', gap: 4,
                          paddingBottom: 8,
                          fontSize: 12, fontWeight: 700,
                          color: riskDelta > 0 ? '#ef4444' : riskDelta < 0 ? '#22c55e' : 'rgba(255,255,255,0.3)',
                        }}>
                          <span style={{ fontSize: 10 }}>
                            {riskDelta > 0 ? '▲' : riskDelta < 0 ? '▼' : '→'}
                          </span>
                          {riskDelta > 0 ? '+' : ''}{riskDelta.toFixed(1)} Pkt
                        </div>
                      )}
                    </>
                  )}
                </div>

                {/* Horizontal stacked bar */}
                <div style={{
                  display: 'flex', height: 12, borderRadius: 6, overflow: 'hidden',
                  background: 'rgba(255,255,255,0.05)', marginBottom: 12,
                }}>
                  {highPct > 0 && (
                    <div style={{
                      width: `${highPct}%`, background: '#ef4444',
                      boxShadow: '0 0 8px #ef444466',
                      transition: 'width 0.6s cubic-bezier(0.4,0,0.2,1)',
                    }} />
                  )}
                  {mediumPct > 0 && (
                    <div style={{
                      width: `${mediumPct}%`, background: '#f59e0b',
                      boxShadow: '0 0 8px #f59e0b66',
                      transition: 'width 0.6s cubic-bezier(0.4,0,0.2,1)',
                    }} />
                  )}
                  {lowPct > 0 && (
                    <div style={{
                      width: `${lowPct}%`, background: '#22c55e',
                      boxShadow: '0 0 8px #22c55e66',
                      transition: 'width 0.6s cubic-bezier(0.4,0,0.2,1)',
                    }} />
                  )}
                </div>

                {/* Legend */}
                <div style={{ display: 'flex', gap: 20 }}>
                  {[
                    { label: 'HIGH', count: highCount, pct: highPct, prevCount: high.prev_count, color: '#ef4444' },
                    { label: 'MEDIUM', count: mediumCount, pct: mediumPct, prevCount: medium.prev_count, color: '#f59e0b' },
                    { label: 'LOW', count: lowCount, pct: lowPct, prevCount: low.prev_count, color: '#22c55e' },
                  ].map(r => {
                    const delta = r.prevCount != null ? r.count - r.prevCount : null;
                    return (
                      <div key={r.label} style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                          <div style={{ width: 8, height: 8, borderRadius: '50%', background: r.color, boxShadow: `0 0 6px ${r.color}88` }} />
                          <span style={{ fontSize: 10, fontWeight: 600, color: r.color }}>{r.label}</span>
                        </div>
                        <div style={{ fontSize: 20, fontWeight: 800, color: r.color, letterSpacing: '-1px' }}>
                          {r.count}
                        </div>
                        <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)' }}>
                          {Number(r.pct).toFixed(1)}%
                          {delta != null && delta !== 0 && (
                            <span style={{ color: r.label === 'LOW' ? (delta > 0 ? '#22c55e' : '#ef4444') : (delta > 0 ? '#ef4444' : '#22c55e'), marginLeft: 6 }}>
                              {delta > 0 ? '+' : ''}{delta} vs. Vorperiode
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              )}

              {/* ── Check-in-Signale (additiv, beruehrt den D3-Score nicht) ── */}
              {checkin && checkin.verfuegbar === true && (
                <div style={{ marginBottom: 24, padding: '20px', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 14 }}>
                  <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.28)', letterSpacing: '0.2em', textTransform: 'uppercase', marginBottom: 16 }}>
                    Kontinuierliche Check-in-Signale
                  </div>
                  {checkin.mood && checkin.mood.verfuegbar === true && (() => {
                    const r = checkin.mood.richtung;
                    const col = r === 'negativ' ? '#ef4444' : r === 'positiv' ? '#22c55e' : '#f59e0b';
                    const rLabel = r === 'negativ' ? 'negativ' : r === 'positiv' ? 'positiv' : 'stabil';
                    const d = checkin.mood.delta;
                    const dStr = d != null ? `${Number(d) > 0 ? '+' : ''}${Number(d).toFixed(1).replace('.', ',')}` : null;
                    return (
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 12 }}>
                        <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)' }}>Stimmungstrend (4 Wochen)</div>
                        <div style={{ fontSize: 20, fontWeight: 800, color: col, letterSpacing: '-0.5px' }}>{rLabel}</div>
                        {dStr && <div style={{ fontSize: 12, color: col, fontWeight: 600 }}>· {dStr}</div>}
                      </div>
                    );
                  })()}
                  {checkin.hinweis && (
                    <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', lineHeight: 1.6 }}>{checkin.hinweis}</div>
                  )}
                  <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', lineHeight: 1.6, marginTop: 4 }}>
                    Fließen mit voller Datenanbindung in den Retention-Score ein.
                  </div>
                </div>
              )}

              {/* ── Block 2: Abteilungs-Vergleich ── */}
              {departments.length > 0 && (
                <div style={{ marginBottom: 24 }}>
                  <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.28)', letterSpacing: '0.2em', textTransform: 'uppercase', marginBottom: 14 }}>
                    Abteilungs-Vergleich
                    {selectedDept && (
                      <span style={{ color: '#3b82f6', marginLeft: 8, letterSpacing: 0, textTransform: 'none', cursor: 'pointer' }}
                        onClick={() => { setSelectedDept(null); fetchData(null); }}>
                        ✕ Filter aufheben
                      </span>
                    )}
                  </div>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))',
                    gap: 10,
                  }}>
                    {departments.map((dept, i) => {
                      const isSelected = selectedDept === dept.name;
                      const deptTotal = (dept.high_count ?? 0) + (dept.medium_count ?? 0) + (dept.low_count ?? 0);
                      const dScore = dept.avg_risk_score != null ? parseFloat(dept.avg_risk_score) : null;
                      const hPct = deptTotal > 0 ? (dept.high_count ?? 0) / deptTotal * 100 : 0;
                      const mPct = deptTotal > 0 ? (dept.medium_count ?? 0) / deptTotal * 100 : 0;
                      const lPct = deptTotal > 0 ? (dept.low_count ?? 0) / deptTotal * 100 : 0;
                      return (
                        <div
                          key={dept.name ?? i}
                          onClick={() => handleDeptClick(dept.name)}
                          style={{
                            padding: '16px',
                            background: isSelected ? 'rgba(59,130,246,0.08)' : 'rgba(255,255,255,0.03)',
                            border: `1px solid ${isSelected ? 'rgba(59,130,246,0.4)' : 'rgba(255,255,255,0.07)'}`,
                            borderRadius: 12, cursor: 'pointer',
                            transition: 'all 0.15s ease',
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                            <div style={{ fontSize: 12, fontWeight: 600, color: 'rgba(255,255,255,0.8)' }}>
                              {dept.name}
                            </div>
                            <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.3)' }}>
                              {dept.employee_count ?? deptTotal} MA
                            </div>
                          </div>

                          {/* Mini stacked bar */}
                          <div style={{
                            display: 'flex', height: 6, borderRadius: 3, overflow: 'hidden',
                            background: 'rgba(255,255,255,0.05)', marginBottom: 10,
                          }}>
                            {hPct > 0 && <div style={{ width: `${hPct}%`, background: '#ef4444' }} />}
                            {mPct > 0 && <div style={{ width: `${mPct}%`, background: '#f59e0b' }} />}
                            {lPct > 0 && <div style={{ width: `${lPct}%`, background: '#22c55e' }} />}
                          </div>

                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div style={{ display: 'flex', gap: 8, fontSize: 10 }}>
                              <span style={{ color: '#ef4444', fontWeight: 600 }}>{dept.high_count ?? 0}</span>
                              <span style={{ color: '#f59e0b', fontWeight: 600 }}>{dept.medium_count ?? 0}</span>
                              <span style={{ color: '#22c55e', fontWeight: 600 }}>{dept.low_count ?? 0}</span>
                            </div>
                            {dScore != null && (
                              <div style={{ fontSize: 14, fontWeight: 800, color: riskColor(dScore), letterSpacing: '-0.5px' }}>
                                {dScore.toFixed(0)}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* ── Block 3: Signal-Heatmap ── */}
              {Object.keys(signals).length > 0 && (
                <div style={{
                  marginBottom: 24, padding: '20px',
                  background: 'rgba(255,255,255,0.03)',
                  border: '1px solid rgba(255,255,255,0.07)',
                  borderRadius: 14,
                }}>
                  <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.28)', letterSpacing: '0.2em', textTransform: 'uppercase', marginBottom: 16 }}>
                    Risiko-Signale
                    {selectedDept && <span style={{ color: '#3b82f6', letterSpacing: 0, textTransform: 'none' }}> — {selectedDept}</span>}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {Object.entries(signals)
                      .filter(([, v]) => v && (v.count > 0 || v.pct > 0))
                      .sort((a, b) => (b[1].count ?? 0) - (a[1].count ?? 0))
                      .map(([key, sig], i) => {
                        const color = SIGNAL_COLORS[key] ?? '#6b7280';
                        const label = SIGNAL_LABELS[key] ?? key;
                        const pct = sig.pct ?? 0;
                        return (
                          <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            <div style={{ width: 110, fontSize: 11, color: 'rgba(255,255,255,0.6)', fontWeight: 500, flexShrink: 0 }}>
                              {label}
                            </div>
                            <AnimatedBar value={pct} color={color} delay={i * 60} />
                            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.55)', whiteSpace: 'nowrap', minWidth: 100, textAlign: 'right' }}>
                              {sig.count} ({Number(pct).toFixed(1)}%)
                              {sig.avg_days != null && (
                                <span style={{ color: 'rgba(255,255,255,0.3)', marginLeft: 4 }}>
                                  Ø {sig.avg_days}d
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    {Object.values(signals).every(v => !v || v.count === 0) && (
                      <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.25)', fontStyle: 'italic' }}>
                        Keine aktiven Risiko-Signale
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* ── Block 4: Handlungsempfehlungen ── */}
              {actions.length > 0 && (
                <div style={{ marginBottom: 20 }}>
                  <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.28)', letterSpacing: '0.2em', textTransform: 'uppercase', marginBottom: 12 }}>
                    Handlungsempfehlungen
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {actions.map((a, i) => {
                      const sevColor = a.severity === 'high' ? '#ef4444' : a.severity === 'medium' ? '#f59e0b' : '#22c55e';
                      return (
                        <div key={i} style={{
                          display: 'flex', alignItems: 'flex-start', gap: 10,
                          padding: '12px 16px',
                          background: `${sevColor}08`,
                          border: `1px solid ${sevColor}20`,
                          borderRadius: 10,
                        }}>
                          <div style={{
                            width: 6, height: 6, borderRadius: '50%',
                            background: sevColor, boxShadow: `0 0 6px ${sevColor}88`,
                            flexShrink: 0, marginTop: 5,
                          }} />
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.7)', lineHeight: 1.6 }}>
                              {a.text}
                            </div>
                            {a.signal && (
                              <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.25)', marginTop: 3 }}>
                                Signal: {SIGNAL_LABELS[a.signal] ?? a.signal}
                              </div>
                            )}
                          </div>
                          <div style={{
                            padding: '2px 8px', borderRadius: 20, fontSize: 9, fontWeight: 600,
                            background: `${sevColor}14`, border: `1px solid ${sevColor}40`,
                            color: sevColor, flexShrink: 0, textTransform: 'uppercase',
                          }}>
                            {a.severity}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* ── Fußzeile ── */}
              <div style={{
                padding: '10px 14px',
                background: 'rgba(255,255,255,0.02)',
                borderRadius: 9, borderLeft: '3px solid #10B981',
                fontSize: 11, color: 'rgba(255,255,255,0.3)', lineHeight: 1.6,
              }}>
                {surveyMode
                  ? <>Retention basiert auf anonymer Kulturumfrage — keine individuellen Risk-Köpfe · {period}</>
                  : <>Retention-Risiko basiert auf Mentoring-Aktivität, Umfragedaten und Rollenmerkmalen · {totalEmp} Mitarbeiter · {period}</>}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
